﻿import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createAsignacion,
  getAsignacionesBundle,
  updateAsignacion,
  toggleAsignacionStatus,
  deleteAsignacion,
} from "../lib/asignacionesApi";
import { useAuth } from "@/context/auth.js";
import AsignacionesTable from "../components/asignaciones/AsignacionesTable.jsx";

function personLabel(persona) {
  const nombre = persona?.nombre || "";
  const apellido = persona?.apellido || "";
  return (
    persona?.full_name?.trim() ||
    [nombre, apellido].filter(Boolean).join(" ").trim() ||
    persona?.email ||
    "Sin nombre"
  );
}

function actividadLabel(item) {
  return item?.name || item?.nombre || item?.title || "Sin nombre";
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function extractBundle(result) {
  const level0 = result ?? {};
  const level1 = level0?.data ?? {};
  const level2 = level1?.data ?? {};
  const candidates = [level0, level1, level2];

  for (const node of candidates) {
    const catalogs = node?.catalogs ?? node?.catalog ?? node?.catalogos ?? null;

    const personal =
      asArray(catalogs?.personal).length > 0
        ? catalogs.personal
        : asArray(catalogs?.people).length > 0
          ? catalogs.people
          : asArray(node?.personal).length > 0
            ? node.personal
            : asArray(node?.people);

    const geofences =
      asArray(catalogs?.geofences).length > 0
        ? catalogs.geofences
        : asArray(catalogs?.geocercas).length > 0
          ? catalogs.geocercas
          : asArray(node?.geofences).length > 0
            ? node.geofences
            : asArray(node?.geocercas);

    const activities =
      asArray(catalogs?.activities).length > 0
        ? catalogs.activities
        : asArray(catalogs?.actividades).length > 0
          ? catalogs.actividades
          : asArray(node?.activities).length > 0
            ? node.activities
            : asArray(node?.actividades);

    const asignaciones =
      asArray(node?.asignaciones).length > 0
        ? node.asignaciones
        : asArray(node?.assignments).length > 0
          ? node.assignments
          : asArray(node?.rows);

    if (
      catalogs ||
      personal.length > 0 ||
      geofences.length > 0 ||
      activities.length > 0 ||
      asignaciones.length > 0
    ) {
      return {
        personas: personal,
        geocercas: geofences,
        actividades: activities,
        asignaciones,
      };
    }
  }

  return {
    personas: [],
    geocercas: [],
    actividades: [],
    asignaciones: [],
  };
}


export default function AsignacionesPage() {
  const { t } = useTranslation();
  const { activeOrgId } = useAuth();

  const [personas, setPersonas] = useState([]);
  const [geocercas, setGeocercas] = useState([]);
  const [actividades, setActividades] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);

  const [selectedPersonId, setSelectedPersonId] = useState("");
  const [selectedGeocercaId, setSelectedGeocercaId] = useState("");
  const [selectedActivityId, setSelectedActivityId] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [endTimeError, setEndTimeError] = useState("");
  const [status, setStatus] = useState("active");
  const [freqMin, setFreqMin] = useState(5);

  const [editingId, setEditingId] = useState(null);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  const [debugCounts, setDebugCounts] = useState({
    personas: 0,
    geocercas: 0,
    actividades: 0,
    asignaciones: 0,
  });

  // Log de render después de inicializar los estados
  console.log("[Asignaciones] render", { saving, editingId });

  useEffect(() => {
    if (!activeOrgId) return;
    loadAll();
  }, [activeOrgId]);

  async function loadAll() {
    try {
      console.log("[AsignacionesPage] getAsignacionesBundle request org_id:", activeOrgId);
      const result = await getAsignacionesBundle(activeOrgId);
      console.log("[AsignacionesPage] raw bundle response:", result);

      if (result?.error) {
        throw new Error(result.error.message || "Error al cargar asignaciones");
      }

      const next = extractBundle(result);

      setDebugCounts({
        personas: next.personas.length,
        geocercas: next.geocercas.length,
        actividades: next.actividades.length,
        asignaciones: next.asignaciones.length,
      });

      setPersonas(next.personas);
      setGeocercas(next.geocercas);
      setActividades(next.actividades);
      setAsignaciones(next.asignaciones);
      setError("");
    } catch (e) {
      console.error("[AsignacionesPage] loadAll failed:", e);
      setError("Error al cargar datos de asignaciones.");
    }
  }

  const personasDisponibles = useMemo(() => {
    return personas.filter((p) => {
      const id = p?.id ?? p?.personal_id ?? null;
      return !!id;
    });
  }, [personas]);

  const selectedPerson = useMemo(() => {
    return (
      personasDisponibles.find(
        (p) => String(p?.id ?? p?.personal_id ?? "") === String(selectedPersonId)
      ) || null
    );
  }, [personasDisponibles, selectedPersonId]);

  const resolvedSelectedPersonId =
    selectedPerson?.id ?? selectedPerson?.personal_id ?? null;

  const resolvedSelectedTrackerUserId = selectedPerson?.user_id ?? null;

  const geofenceOptions = useMemo(() => {
    return geocercas
      .filter((g) => g.active !== false)
      .map((g) => ({
        value: g?.id ?? null,
        label: g?.name || `Geocerca ${g?.id ?? ""}`,
      }))
      .filter((opt) => !!opt.value);
  }, [geocercas]);

  async function handleSubmit(e) {
    e?.preventDefault?.();

    console.log("[Asignaciones] handleSubmit start", {
      saving,
      editingId,
      form: {
        selectedPersonId,
        selectedGeocercaId,
        selectedActivityId,
        startTime,
        endTime,
        freqMin,
        status,
      },
    });

    setError("");
    setSuccess("");
    setEndTimeError("");


    if (!activeOrgId) {
      console.log("[Asignaciones] validation fail: missing org", { form: {
        selectedPersonId,
        selectedGeocercaId,
        selectedActivityId,
        startTime,
        endTime,
        freqMin,
        status,
      }});
      setError(t("asignaciones.error.noActiveOrg"));
      return;
    }

    if (!resolvedSelectedPersonId) {
      console.log("[Asignaciones] validation fail: missing person", { form: {
        selectedPersonId,
        selectedGeocercaId,
        selectedActivityId,
        startTime,
        endTime,
        freqMin,
        status,
      }});
      setError(t("asignaciones.error.invalidPerson"));
      return;
    }


    if (!selectedGeocercaId) {
      console.log("[Asignaciones] validation fail: missing geofence", { form: {
        selectedPersonId,
        selectedGeocercaId,
        selectedActivityId,
        startTime,
        endTime,
        freqMin,
        status,
      }});
      setError(t("asignaciones.error.missingGeofence"));
      return;
    }

    if (!selectedActivityId) {
      console.log("[Asignaciones] validation fail: missing activity", { form: {
        selectedPersonId,
        selectedGeocercaId,
        selectedActivityId,
        startTime,
        endTime,
        freqMin,
        status,
      }});
      setError(t("asignaciones.error.missingActivity"));
      return;
    }

    if (!startTime) {
      console.log("[Asignaciones] validation fail: missing start time", { form: {
        selectedPersonId,
        selectedGeocercaId,
        selectedActivityId,
        startTime,
        endTime,
        freqMin,
        status,
      }});
      setError(t("asignaciones.error.missingStart"));
      return;
    }

    if (endTime && startTime && new Date(endTime) < new Date(startTime)) {
      console.log("[Asignaciones] validation fail: invalid end time", { form: {
        selectedPersonId,
        selectedGeocercaId,
        selectedActivityId,
        startTime,
        endTime,
        freqMin,
        status,
      }});
      const msg = t("asignaciones.error.endBeforeStart");
      setEndTimeError(msg);
      setError(msg);
      return;
    }

    const parsedFreqMin = Number(freqMin);
    if (!Number.isFinite(parsedFreqMin) || parsedFreqMin < 5) {
      console.log("[Asignaciones] validation fail: invalid frequency", { form: {
        selectedPersonId,
        selectedGeocercaId,
        selectedActivityId,
        startTime,
        endTime,
        freqMin,
        status,
      }});
      setError(t("asignaciones.error.invalidFrequency"));
      return;
    }

    setSaving(true);

    const payload = {
      id: editingId || undefined,
      personal_id: resolvedSelectedPersonId,
      user_id: resolvedSelectedTrackerUserId,
      org_id: activeOrgId,
      geofence_id: selectedGeocercaId || null,
      activity_id: selectedActivityId || null,
      start_time: startTime ? new Date(startTime).toISOString() : null,
      end_time: endTime ? new Date(endTime).toISOString() : null,
      frequency_minutes: parsedFreqMin,
      frecuencia_envio_sec: parsedFreqMin * 60,
      status,
    };

    try {
      console.log("[Asignaciones] handleSubmit before save");
      if (editingId) {
        await updateAsignacion(editingId, payload, activeOrgId);
      } else {
        await createAsignacion(payload, activeOrgId);
      }

      await loadAll();

      setSuccess(
        editingId
          ? t("asignaciones.banner.updated")
          : t("asignaciones.messages.createSuccess")
      );

      setSelectedPersonId("");
      setSelectedGeocercaId("");
      setSelectedActivityId("");
      setStartTime("");
      setEndTime("");
      setEndTimeError("");
      setStatus("active");
      setFreqMin(5);
      setEditingId(null);
      console.log("[Asignaciones] handleSubmit success");
    } catch (err) {
      console.log("[Asignaciones] handleSubmit error", err);
      setError(
        err?.message ||
          (editingId
            ? t("asignaciones.error.update")
            : t("asignaciones.error.save"))
      );
    } finally {
      setSaving(false);
      console.log("[Asignaciones] handleSubmit finally -> saving false");
    }
  }

  function handleEdit(row) {
    setError("");
    setSuccess("");
    setEndTimeError("");

    setEditingId(row?.id || null);
    setSelectedPersonId(String(row?.personal_id || row?.org_people_id || ""));
    setSelectedGeocercaId(String(row?.geofence_id || row?.geocerca_id || ""));
    setSelectedActivityId(String(row?.activity_id || ""));
    setStartTime(row?.start_time ? row.start_time.slice(0, 16) : "");
    setEndTime(row?.end_time ? row.end_time.slice(0, 16) : "");

    const rowStatus = String(row?.status || row?.estado || "").toLowerCase();
    setStatus(
      rowStatus === "inactive" || rowStatus === "inactiva" ? "inactive" : "active"
    );

    setFreqMin(
      row?.frequency_minutes
        ? Number(row.frequency_minutes)
        : row?.frecuencia_envio_sec
          ? Math.round(Number(row.frecuencia_envio_sec) / 60)
          : 5
    );

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleToggleStatus(row) {
    try {
      setError("");
      setSuccess("");

      const id = row?.id;
      if (!id) return;

      const rawStatus = String(row?.status || row?.estado || "").toLowerCase();
      const isActive = rawStatus === "active" || rawStatus === "activa";
      const current = isActive ? "active" : "inactive";
      const next = current === "active" ? "inactive" : "active";

      await toggleAsignacionStatus(id, current, activeOrgId);
      await loadAll();

      setSuccess(
        next === "active"
          ? t("asignaciones.messages.activatedSuccess")
          : t("asignaciones.messages.deactivatedSuccess")
      );
    } catch (e) {
      console.error("[AsignacionesPage] toggle status failed:", e);
      setError(e?.message || t("asignaciones.messages.toggleStatusError"));
    }
  }

  async function handleDelete(id) {
    try {
      setError("");
      setSuccess("");

      if (!id) return;

      await deleteAsignacion(id, activeOrgId);
      await loadAll();
      setSuccess(t("asignaciones.messages.deleteSuccess"));
    } catch (e) {
      console.error("[AsignacionesPage] delete failed:", e);
      setError(e?.message || t("asignaciones.messages.deleteError"));
    }
  }

  function handleCancelEdit() {
    setEditingId(null);
    setSelectedPersonId("");
    setSelectedGeocercaId("");
    setSelectedActivityId("");
    setStartTime("");
    setEndTime("");
    setEndTimeError("");
    setStatus("active");
    setFreqMin(5);
    setError("");
    setSuccess("");
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6 lg:p-8">

      <section className="relative overflow-hidden rounded-3xl border border-emerald-200/70 bg-gradient-to-br from-emerald-950 via-emerald-800 to-teal-700 px-5 py-6 text-white shadow-xl shadow-emerald-950/15 md:px-7 md:py-7">
        <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-10 h-56 w-56 rounded-full bg-lime-200/10 blur-3xl" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-50">
              {t("asignaciones.heroEyebrow", { defaultValue: "Operación" })}
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">
                {t("asignaciones.title", { defaultValue: "Asignaciones" })}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-emerald-50/90">
                {t("asignaciones.heroSubtitle", { defaultValue: "Relaciona personas, geocercas, actividades y horarios para coordinar el trabajo." })}
              </p>
            </div>
          </div>
        </div>
      </section>
      <div className="mb-6 rounded-3xl border border-emerald-100 bg-white p-5 shadow-lg shadow-emerald-950/5">
        <h2 className="mb-4 text-2xl font-semibold text-gray-900">
          {editingId ? t("asignaciones.form.editTitle") : t("asignaciones.form.newTitle")}
        </h2>

        {error ? (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {success ? (
          <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {success}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-800">
              {t("asignaciones.fields.person")}
            </label>
            <select
              className="w-full rounded-xl border border-emerald-100 bg-white px-3.5 py-2.5 text-gray-900 shadow-sm shadow-emerald-900/5"
              value={selectedPersonId}
              onChange={(e) => setSelectedPersonId(e.target.value)}
            >
              <option value="">{t('asignaciones.form.select')}</option>
              {personasDisponibles.map((p) => {
                const id = p?.id ?? p?.personal_id ?? null;
                if (!id) return null;
                return (
                  <option key={id} value={id}>
                    {personLabel(p)}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-800">
              {t("asignaciones.fields.geofence")}
            </label>
            <select
              className="w-full rounded-xl border border-emerald-100 bg-white px-3.5 py-2.5 text-gray-900 shadow-sm shadow-emerald-900/5"
              value={selectedGeocercaId}
              onChange={(e) => setSelectedGeocercaId(e.target.value)}
            >
              <option value="">{t('asignaciones.form.select')}</option>
              {geofenceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-800">
              {t("asignaciones.fields.activity")}
            </label>
            <select
              className="w-full rounded-xl border border-emerald-100 bg-white px-3.5 py-2.5 text-gray-900 shadow-sm shadow-emerald-900/5"
              value={selectedActivityId}
              onChange={(e) => setSelectedActivityId(e.target.value)}
            >
              <option value="">{t('asignaciones.form.select')}</option>
              {actividades.map((a) => (
                <option key={a.id} value={a.id}>
                  {actividadLabel(a)}
                </option>
              ))}
            </select>
            {actividades.length === 0 ? (
              <p className="mt-2 text-sm text-amber-700">
                {t("asignaciones.form.noActivitiesHint")}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800">
                {t("asignaciones.fields.startAt")}
              </label>
              <input
                type="datetime-local"
                className="w-full rounded-xl border border-emerald-100 bg-white px-3.5 py-2.5 text-gray-900 shadow-sm shadow-emerald-900/5"
                value={startTime}
                onChange={(e) => {
                  const nextStart = e.target.value;
                  setStartTime(nextStart);

                  if (endTime && nextStart && new Date(endTime) < new Date(nextStart)) {
                    setEndTimeError(
                      t("asignaciones.error.endBeforeStart")
                    );
                  } else {
                    setEndTimeError("");
                  }
                }}
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800">
                {t("asignaciones.fields.endAt")}
              </label>
              <input
                type="datetime-local"
                min={startTime || undefined}
                className="w-full rounded-xl border border-emerald-100 bg-white px-3.5 py-2.5 text-gray-900 shadow-sm shadow-emerald-900/5"
                value={endTime}
                onChange={(e) => {
                  const nextEnd = e.target.value;
                  setEndTime(nextEnd);

                  if (startTime && nextEnd && new Date(nextEnd) < new Date(startTime)) {
                    setEndTimeError(
                      t("asignaciones.error.endBeforeStart")
                    );
                  } else {
                    setEndTimeError("");
                  }
                }}
              />
              {endTimeError ? (
                <p className="mt-1 text-sm text-red-600">{endTimeError}</p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800">
                {t("asignaciones.fields.status")}
              </label>
              <select
                className="w-full rounded-xl border border-emerald-100 bg-white px-3.5 py-2.5 text-gray-900 shadow-sm shadow-emerald-900/5"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="active">{t('asignaciones.form.statusActive')}</option>
                <option value="inactive">{t('asignaciones.form.statusInactive')}</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800">
                {t("asignaciones.fields.frequencyMinutes")}
              </label>
              <input
                type="number"
                min="5"
                className="w-full rounded-xl border border-emerald-100 bg-white px-3.5 py-2.5 text-gray-900 shadow-sm shadow-emerald-900/5"
                value={freqMin}
                onChange={(e) => setFreqMin(e.target.value)}
              />
            </div>
          </div>

          {error ? (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center rounded-xl bg-emerald-600 px-4 py-2 font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {saving
                ? editingId
                  ? `${t("asignaciones.form.updateButton")}...`
                  : `${t("asignaciones.form.saveButton")}...`
                : editingId
                  ? t("asignaciones.form.updateButton")
                  : t("asignaciones.form.saveButton")}
            </button>

            {editingId ? (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="inline-flex items-center rounded-xl border border-emerald-200 bg-white px-4 py-2 font-medium text-gray-700 hover:bg-emerald-50"
              >
                {t("asignaciones.actions.cancel")}
              </button>
            ) : null}
          </div>
        </form>

        <div className="mt-4 text-xs text-gray-500">
          Debug: personas {debugCounts.personas} · geocercas {debugCounts.geocercas} · actividades {debugCounts.actividades} · asignaciones {debugCounts.asignaciones}
        </div>
      </div>

      <AsignacionesTable
        asignaciones={asignaciones}
        people={personas}
        geofences={geocercas}
        activities={actividades}
        onEdit={handleEdit}
        onToggleStatus={handleToggleStatus}
        onDelete={handleDelete}
      />
    </div>
  );
}